import { html, cx, useState, useEffect, useRef, useReducer, useCallback } from '../h.js'
import { Icon } from '../icons.js'
import { t } from '../i18n.js'
import { shortId, listTime } from '../format.js'
import { Avatar, Modal, useToast } from '../components.js'
import { useApp, useStore } from '../store.js'
import { preparePhoto, describeVideo } from '../media-prep.js'

/**
 * Stories: the bar above the chat list, posting one, and watching them.
 *
 * The core (packages/pine-core/lib/stories.js) does the cryptography and the
 * talking to the server; this is only what they look like. Media is held as
 * an object URL while it is on screen and let go the moment it is not.
 */

const PHOTO_MS = 6000
// How old the feed may be when the chat list is looked at again.
const STALE_MS = 30 * 1000
const VIDEO_MAX_SECONDS = 60
const STORY_FILE_MAX = 24 * 1024 * 1024

/** A name and a face for an account, from what this device already knows. */
function personOf (client, account) {
  if (account === client.identity.id) {
    return { name: client.identity.profile.name || t('stories.me'), avatar: client.identity.profile.avatar || null }
  }
  for (const record of client.summaries.values()) {
    if (record.peer === account && !record.deleted) return { name: record.name || shortId(account), avatar: record.avatar || null }
  }
  return { name: shortId(account), avatar: null }
}

function useStories (client) {
  const [, bump] = useReducer((n) => n + 1, 0)
  useEffect(() => (client.stories ? client.on('stories', () => bump()) : undefined), [client])

  /*
   * Fresh whenever the list is looked at.
   *
   * The feed is a question to the server, and it was asked every few
   * minutes: a friend's new story, or the count of who has watched yours,
   * showed up long after it happened — or not until the app was restarted.
   * Coming back to the list, or to the app, is when it is worth asking.
   */
  useEffect(() => {
    const stories = client.stories
    if (!stories || !stories.enabled) return
    const fresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      if (Date.now() - stories.fetchedAt > STALE_MS) stories.refresh().catch(() => {})
    }
    fresh()
    document.addEventListener('visibilitychange', fresh)
    window.addEventListener('focus', fresh)
    return () => {
      document.removeEventListener('visibilitychange', fresh)
      window.removeEventListener('focus', fresh)
    }
  }, [client])

  return client.stories && client.stories.enabled ? client.stories.byAuthor() : null
}

/* ------------------------------------------------------------------ *
 * The bar
 * ------------------------------------------------------------------ */

export function StoriesBar () {
  const store = useApp()
  const toast = useToast()
  const client = store.client
  const groups = useStories(client)
  const fileRef = useRef(null)
  const [preparing, setPreparing] = useState(false)
  if (!groups) return null

  const mine = groups.find((g) => g.mine)
  const others = groups.filter((g) => !g.mine)
  const me = personOf(client, client.identity.id)

  function open (author) {
    store.set({ storyViewer: { order: groups.map((g) => g.author), author, index: 0 } })
  }

  async function picked (file) {
    if (!file) return
    setPreparing(true)
    try {
      const item = await prepareStoryFile(file)
      store.showDialog({ type: 'story-new', item })
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    } finally {
      setPreparing(false)
    }
  }

  return html`
    <div class="stories-bar" role="list">
      <div class="story-chip" role="listitem">
        <button class="story-face" title=${mine ? t('stories.mine') : t('stories.add')}
                onClick=${() => (mine ? open(client.identity.id) : fileRef.current && fileRef.current.click())}>
          <span class=${cx('story-ring', mine ? 'seen' : 'none')}>
            <${Avatar} name=${me.name} id=${client.identity.id} src=${me.avatar} />
          </span>
          <span class="story-add" onClick=${(e) => { e.stopPropagation(); if (fileRef.current) fileRef.current.click() }}
                title=${t('stories.add')}>
            ${preparing ? html`<span class="spinner" style="width:10px;height:10px"></span>` : Icon.plus(12)}
          </span>
        </button>
        <span class="story-name">${t('stories.mine')}</span>
      </div>

      ${others.map((group) => {
        const person = personOf(client, group.author)
        return html`
          <div key=${group.author} class="story-chip" role="listitem">
            <button class="story-face" onClick=${() => open(group.author)} title=${person.name}>
              <span class=${cx('story-ring', group.unseen ? 'unseen' : 'seen')}>
                <${Avatar} name=${person.name} id=${group.author} src=${person.avatar} />
              </span>
            </button>
            <span class="story-name">${person.name}</span>
          </div>`
      })}

      <input ref=${fileRef} type="file" accept="image/*,video/*" class="hidden"
             onChange=${(e) => { const input = e.target; picked(input.files && input.files[0]).finally(() => { input.value = '' }) }} />
    </div>`
}

/** Read, check and shrink a picked file into what a story carries. */
async function prepareStoryFile (file) {
  const bytes = new Uint8Array(await file.arrayBuffer())
  const mime = file.type || ''
  if (/^image\//.test(mime)) {
    const photo = await preparePhoto(bytes, { name: file.name, mime })
    if (!photo) throw new Error(t('stories.unreadable'))
    if (photo.bytes.byteLength > STORY_FILE_MAX) throw new Error(t('stories.tooBig'))
    return { bytes: photo.bytes, mime: photo.mime || mime, width: photo.width, height: photo.height, duration: 0 }
  }
  if (/^video\//.test(mime)) {
    if (bytes.byteLength > STORY_FILE_MAX) throw new Error(t('stories.tooBig'))
    const info = await describeVideo(bytes, { mime })
    if (info.duration && info.duration > VIDEO_MAX_SECONDS) throw new Error(t('stories.tooLong'))
    return { bytes, mime, width: info.width || 0, height: info.height || 0, duration: info.duration || 0 }
  }
  throw new Error(t('stories.unsupported'))
}

/* ------------------------------------------------------------------ *
 * Posting
 * ------------------------------------------------------------------ */

export function StoryComposer ({ item, onClose }) {
  const store = useApp()
  const toast = useToast()
  const [caption, setCaption] = useState('')
  const [busy, setBusy] = useState(false)
  const [url] = useState(() => URL.createObjectURL(new Blob([item.bytes], { type: item.mime })))
  useEffect(() => () => URL.revokeObjectURL(url), [url])

  async function publish () {
    setBusy(true)
    try {
      await store.client.stories.publish({
        data: item.bytes, mime: item.mime, caption: caption.trim(),
        width: item.width, height: item.height, duration: item.duration
      })
      toast(t('stories.published'))
      onClose()
    } catch (err) {
      toast(err.reason === 'too_large' ? t('stories.tooBig') : (t('stories.failed') + (err.message ? ': ' + err.message : '')), { error: true })
    } finally {
      setBusy(false)
    }
  }

  return html`
    <${Modal} title=${t('stories.title')} onClose=${busy ? () => {} : onClose}
      footer=${html`
        <button class="btn btn-ghost" disabled=${busy} onClick=${onClose}>${t('action.cancel')}</button>
        <button class="btn btn-primary" disabled=${busy} onClick=${publish}>
          ${busy ? html`<div class="spinner"></div>` : t('stories.publish')}
        </button>`}>
      <div class="story-preview">
        ${/^video\//.test(item.mime)
          ? html`<video src=${url} controls playsinline muted loop></video>`
          : html`<img src=${url} alt="" />`}
      </div>
      <textarea class="input" rows="2" maxlength="1024" placeholder=${t('stories.caption')}
                value=${caption} onInput=${(e) => setCaption(e.target.value)}></textarea>
      <p class="dim small" style="margin:8px 0 0">${t('stories.audience')}</p>
    <//>`
}

/* ------------------------------------------------------------------ *
 * Watching
 * ------------------------------------------------------------------ */

export function StoryViewer () {
  const store = useStore()
  const toast = useToast()
  const client = store.client
  const view = store.get('storyViewer')
  const groups = client.stories ? client.stories.byAuthor() : []
  const group = view && groups.find((g) => g.author === view.author)
  const story = group && (group.stories[view.index] || group.stories[0])

  const [media, setMedia] = useState(null) // { url, mime, caption, duration }
  const [failed, setFailed] = useState('')
  const [paused, setPaused] = useState(false)
  const [progress, setProgress] = useState(0)
  const [reply, setReply] = useState('')
  const [viewers, setViewers] = useState(null)
  const [muted, setMuted] = useState(false)
  const [views, setViews] = useState(null)
  const videoRef = useRef(null)
  const holdTimer = useRef(null)

  const close = useCallback(() => store.set({ storyViewer: null }), [store])

  const go = useCallback((delta) => {
    if (!view || !group) return close()
    const index = view.index + delta
    if (index >= 0 && index < group.stories.length) return store.set({ storyViewer: { ...view, index } })
    // Past either end of this person's stories: the next or previous person.
    const order = view.order.filter((author) => groups.some((g) => g.author === author))
    const at = order.indexOf(view.author)
    const nextAuthor = order[at + (delta > 0 ? 1 : -1)]
    if (!nextAuthor) return delta > 0 ? close() : store.set({ storyViewer: { ...view, index: 0 } })
    const nextGroup = groups.find((g) => g.author === nextAuthor)
    store.set({ storyViewer: { ...view, author: nextAuthor, index: delta > 0 ? 0 : nextGroup.stories.length - 1 } })
  }, [view, group, groups, store, close])

  // The story itself: downloaded, decrypted, shown, and counted as seen.
  useEffect(() => {
    if (!story) return
    let alive = true
    let url = null
    setMedia(null)
    setFailed('')
    setProgress(0)
    setViewers(null)
    setViews(null)
    // One's own story: the count of who has watched it as it is now, not as
    // it was when the feed was last fetched.
    if (story.mine) {
      client.stories.viewers(story).then((list) => { if (alive) setViews(list.length) }).catch(() => {})
    }
    client.stories.open(story).then((opened) => {
      if (!alive) return
      url = URL.createObjectURL(new Blob([opened.bytes], { type: opened.mime }))
      setMedia({ url, mime: opened.mime, caption: opened.caption, duration: opened.duration })
      client.stories.markSeen(story).catch(() => {})
    }).catch((err) => { if (alive) setFailed(err.reason === 'gone' ? t('stories.gone') : t('err.generic')) })
    return () => {
      alive = false
      if (url) URL.revokeObjectURL(url)
    }
  }, [story && story.id])

  // A photo stays for a few seconds; a video for as long as it plays.
  const isVideo = !!(media && /^video\//.test(media.mime))
  useEffect(() => {
    if (!media || isVideo || paused || viewers) return
    const started = Date.now() - progress * PHOTO_MS
    const timer = setInterval(() => {
      const value = (Date.now() - started) / PHOTO_MS
      if (value >= 1) {
        clearInterval(timer)
        go(1)
      } else {
        setProgress(value)
      }
    }, 50)
    return () => clearInterval(timer)
  }, [media, paused, viewers, isVideo, go])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    if (paused || viewers) return video.pause()
    /*
     * A browser may refuse to start a video with sound that was not started
     * by a tap — Safari on an iPhone always does here, because the video
     * arrives after the tap, once it is downloaded and decrypted. It then sat
     * on its first frame with the bar at zero and the viewer never moved on.
     * Without sound it is allowed; the speaker button turns it back on.
     */
    video.play().catch(() => {
      video.muted = true
      setMuted(true)
      video.play().catch(() => {})
    })
  }, [paused, viewers, media])

  // Escape closes, the arrow keys move.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowRight') go(1)
      if (e.key === 'ArrowLeft') go(-1)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [close, go])

  if (!view) return null
  if (!group || !story) {
    setTimeout(close, 0)
    return null
  }

  const person = personOf(client, group.author)
  const mine = group.mine

  function press (e) {
    if (e.target.closest('.story-foot, .story-head, .story-viewers')) return
    holdTimer.current = setTimeout(() => { holdTimer.current = null; setPaused(true) }, 220)
  }
  function release (e) {
    if (e.target.closest('.story-foot, .story-head, .story-viewers')) return
    if (holdTimer.current) {
      // A tap: the left third goes back, the rest forward.
      clearTimeout(holdTimer.current)
      holdTimer.current = null
      const box = e.currentTarget.getBoundingClientRect()
      go(e.clientX - box.left < box.width / 3 ? -1 : 1)
    } else {
      setPaused(false)
    }
  }

  async function send (text, reaction = false) {
    if (!text.trim()) return
    try {
      let summary = client.directs.find(story.author)
      if (!summary) summary = (await client.startDirectMessage(story.author)).room
      const room = await client.openRoom(summary.id)
      await room.send({ text: text.trim(), extra: { story: { id: story.id, at: story.at, reaction } } })
      setReply('')
      toast(t('stories.sent'))
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    }
  }

  async function showViewers () {
    setPaused(true)
    try {
      setViewers(await client.stories.viewers(story))
    } catch (err) {
      setViewers([])
    }
  }

  async function remove () {
    if (!confirm(t('stories.deleteConfirm'))) return
    try {
      await client.stories.remove(story)
      toast(t('stories.deleted'))
      if (group.stories.length <= 1) close()
    } catch (err) {
      toast(err.message || t('err.generic'), { error: true })
    }
  }

  return html`
    <div class="story-viewer" role="dialog" aria-modal="true"
         onPointerDown=${press} onPointerUp=${release}>
      <div class="story-bars">
        ${group.stories.map((s, i) => html`
          <span key=${s.id} class="story-bar">
            <span class="fill" style=${'width:' + (i < view.index ? 100 : i === view.index ? Math.round(progress * 100) : 0) + '%'}></span>
          </span>`)}
      </div>

      <div class="story-head">
        <${Avatar} name=${person.name} id=${group.author} src=${person.avatar} size="sm" />
        <div class="grow" style="min-width:0">
          <div class="truncate strong">${mine ? t('stories.mine') : person.name}</div>
          <div class="xs" style="opacity:.75">${listTime(story.at)}</div>
        </div>
        ${isVideo && html`
          <button class="icon-btn" aria-label=${muted ? t('stories.unmute') : t('stories.mute')}
                  onClick=${() => {
                    const video = videoRef.current
                    if (video) video.muted = !muted
                    setMuted(!muted)
                  }}>${muted ? Icon.volumeOff(20) : Icon.volume(20)}</button>`}
        <button class="icon-btn" aria-label=${paused ? t('stories.play') : t('stories.pause')}
                onClick=${() => setPaused((p) => !p)}>${paused ? Icon.play ? Icon.play(20) : '▶' : Icon.pause ? Icon.pause(20) : 'Ⅱ'}</button>
        <button class="icon-btn" aria-label=${t('action.close')} onClick=${close}>${Icon.close(22)}</button>
      </div>

      <div class="story-media">
        ${failed
          ? html`<div class="story-failed">${failed}</div>`
          : !media
            ? html`<div class="spinner lg"></div>`
            : isVideo
              ? html`<video ref=${videoRef} src=${media.url} autoplay playsinline muted=${muted}
                       onTimeUpdate=${(e) => { const v = e.currentTarget; if (v.duration) setProgress(v.currentTime / v.duration) }}
                       onEnded=${() => go(1)}></video>`
              : html`<img src=${media.url} alt="" draggable="false" />`}
      </div>

      ${media && media.caption && html`<div class="story-caption">${media.caption}</div>`}

      ${viewers && html`
        <div class="story-viewers" onPointerDown=${(e) => e.stopPropagation()}>
          <div class="row" style="margin-bottom:8px">
            <b class="grow">${t('stories.viewsTitle', { n: viewers.length })}</b>
            <button class="icon-btn" onClick=${() => { setViewers(null); setPaused(false) }}>${Icon.close(18)}</button>
          </div>
          ${viewers.length === 0
            ? html`<div class="dim small">${t('stories.noViews')}</div>`
            : viewers.map((v) => {
              const who = personOf(client, v.account)
              return html`
                <div key=${v.account} class="row gap2" style="padding:6px 0">
                  <${Avatar} name=${who.name} id=${v.account} src=${who.avatar} size="sm" />
                  <span class="grow truncate">${who.name}</span>
                  <span class="xs dim">${listTime(v.at)}</span>
                </div>`
            })}
        </div>`}

      <div class="story-foot">
        ${mine
          ? html`
            <button class="btn btn-soft btn-sm" onClick=${showViewers}>
              ${Icon.eye(16)} ${views !== null ? views : typeof story.views === 'number' ? story.views : '…'}
            </button>
            <span class="grow"></span>
            <button class="btn btn-soft btn-sm" onClick=${remove}>${Icon.trash(16)} ${t('stories.delete')}</button>`
          : html`
            <input class="story-reply" value=${reply} placeholder=${t('stories.reply')}
                   onFocus=${() => setPaused(true)} onBlur=${() => setPaused(false)}
                   onInput=${(e) => setReply(e.target.value)}
                   onKeyDown=${(e) => { if (e.key === 'Enter') send(reply) }} />
            ${reply.trim()
              ? html`<button class="icon-btn" aria-label=${t('action.send')} onClick=${() => send(reply)}>${Icon.send(20)}</button>`
              : html`<button class="icon-btn story-heart" aria-label="❤️" onClick=${() => send('❤️', true)}>❤️</button>`}`}
      </div>
    </div>`
}
